import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import { CJsonTextArea } from "@src/components/CJsonTextArea";
import { useBacktests, useJobPolling, useStrategies } from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import get from "lodash-es/get";
import pick from "lodash-es/pick";
import set from "lodash-es/set";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage, httpStatusOf } from "@src/utils/apiError";
import {
  classifyExitPolicy,
  cloneExitPolicy,
  isRecord,
  runConfigBlock,
} from "@src/utils/exitPolicyClone";
import type { ExitPolicyKey } from "@src/utils/exitPolicyClone";
import { formatBacktestDataFromSource } from "@src/utils/formatters";
import { BACKTEST_ONLY_ENTRY_POLICY_KEYS } from "@src/utils/runConfigSections";
import type { CommonSettingsData, ExchangeEnvironment } from "@src/types";
import type * as TDT from "@tradingdroid/frontends-types";
import { JOB_TYPE_NAMES } from "@tradingdroid/frontends-types";

import "./FromJobModal.scss";

// Book jobs are not created from this modal.
const VALID_JOB_TYPES: readonly string[] = JOB_TYPE_NAMES.filter((type) => type !== "book");

/** Fields accepted by the job create endpoint (mirrors ApiJobCreateBase + discriminant fields). */
const JOB_CREATE_FIELDS = [
  "job_type_name",
  "config",
  "strategy_id",
  "exchange_name",
  "symbol_name",
  "period",
  "from_ts",
  "end_ts",
  "description",
  "priority",
  "target_worker_id",
] as const;

/**
 * Validate pasted JSON looks like a job object.
 * Strict: casts to TDT.ApiJob so all downstream access is type-checked.
 * If local and prod schemas diverge, TS will catch field mismatches at compile time.
 */
const parseJobJson = (
  text: string,
): { job: TDT.ApiJob; error: null } | { job: null; error: string } => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { job: null, error: "Invalid JSON" };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { job: null, error: "JSON must be an object" };
  }

  const obj = parsed as Record<string, unknown>;

  if (!obj.job_type_name) {
    return { job: null, error: "Missing 'job_type_name' field" };
  }
  if (obj.job_type_name === "book") {
    return {
      job: null,
      error: "Book jobs are created with the CLI (api jobs create), not this form.",
    };
  }
  if (!VALID_JOB_TYPES.includes(obj.job_type_name as string)) {
    return { job: null, error: `Invalid job type: '${obj.job_type_name}'` };
  }
  if (!obj.config || typeof obj.config !== "object") {
    return { job: null, error: "Missing or invalid 'config' field" };
  }
  if (!obj.strategy_id || typeof obj.strategy_id !== "number") {
    return { job: null, error: "Missing or invalid 'strategy_id' field" };
  }

  return { job: parsed as TDT.ApiJob, error: null };
};

/** The largest job id the API accepts in a job path (a Postgres integer). */
const MAX_JOB_ID = 2_147_483_647;

/**
 * Build the duplicate origin from this environment's job, as the id check read it, for
 * traceability. The pasted JSON may come from another environment, so it does not supply the link.
 */
const buildDuplicateOrigin = (job: TDT.ApiJob): TDT.ApiJobOriginDuplicate => {
  const origin: TDT.ApiJobOriginDuplicate = {
    type: "duplicate",
    source_job_id: job.id,
  };

  if (job.job_type_name === "backtest" && job.result?.data?.backtest_id != null) {
    origin.backtest_id = job.result.data.backtest_id;
  }

  return origin;
};

/**
 * Whether this environment's job is the pasted one: same type, strategy and creation instant. The
 * jobs API serves the creation time as `create_ts`.
 */
const isSameJob = (pasted: TDT.ApiJob, local: TDT.ApiJob, created: number): boolean =>
  local.job_type_name === pasted.job_type_name &&
  local.strategy_id === pasted.strategy_id &&
  local.create_ts != null &&
  Date.parse(local.create_ts) === created;

/**
 * The origin a copy of a pasted job records. A pasted `id` names a job in the environment that
 * produced the JSON, which need not be this one, while a `duplicate` origin names a job in this
 * one. So the copy records `duplicate` only when this environment returns a job with that id and
 * the same type, strategy and creation time. It records `manual` when the paste has no usable
 * id, when the read answers 404 (no such job, or one the caller does not own), or when the job
 * differs or the pasted creation time cannot be parsed. Other read failures are thrown.
 */
const resolveOrigin = async (job: TDT.ApiJob): Promise<TDT.ApiJobOrigin> => {
  const { id } = job;
  if (!Number.isInteger(id) || id < 1 || id > MAX_JOB_ID) return { type: "manual" };

  const created = job.create_ts ? Date.parse(job.create_ts) : Number.NaN;
  if (Number.isNaN(created)) return { type: "manual" };

  let local: TDT.ApiJob | undefined;
  try {
    // Read identity fields, plus `result` for a backtest's `backtest_id` link.
    // A job has no `execution_id` column, and the API refuses a projection that names one (422).
    local = await tdApi.getJob(id, {
      fields:
        "id,job_type_name,strategy_id,create_ts" +
        (job.job_type_name === "backtest" ? ",result" : ""),
    });
  } catch (error) {
    if (httpStatusOf(error) === 404) return { type: "manual" };
    throw error;
  }
  return local && isSameJob(job, local, created) ? buildDuplicateOrigin(local) : { type: "manual" };
};

const REVERSAL_POLICY_KEY = "cross_direction_reversal";
const FUNDING_ACCOUNTING_KEY = "funding_accounting_version";

/**
 * The run-config block of a pasted job with an `id`, which this modal takes as a stored job, and
 * the block's path. Null when the paste has no `id`, its job type has no run-config block, or the
 * block is missing or is not an object.
 */
const storedRunBlock = (
  job: TDT.ApiJob,
): { block: string[]; target: Record<string, unknown> } | null => {
  const block = runConfigBlock(job.job_type_name);
  if (!job.id || block === null) return null;
  const target: unknown = block.length > 0 ? get(job.config, block) : job.config;
  return isRecord(target) ? { block, target } : null;
};

/**
 * The config path where a copy of this pasted job pins `cross_direction_reversal: legacy`, or null
 * when it pins nothing. A pasted job with an `id` is taken as a stored job, and when its config
 * does not name the policy, the API reads that stored request as legacy. Pinning legacy keeps that
 * policy in the copy, and a request that names the policy is stored as sent without the API reading
 * the origin's source job. A paste without an `id` is left without the key and gets a `manual`
 * origin, and the API decides its policy as for any keyless request with that origin.
 */
const legacyReversalPinPath = (job: TDT.ApiJob): string[] | null => {
  const run = storedRunBlock(job);
  if (!run || REVERSAL_POLICY_KEY in run.target) return null;
  return [...run.block, REVERSAL_POLICY_KEY];
};

/**
 * The path where a stored run copy fills an omitted funding selector with version 1, or null when
 * it fills nothing. A missing or null cost map is created holding the selector. A cost map that is
 * set but is not an object gets no selector.
 */
const legacyFundingPinPath = (job: TDT.ApiJob): string[] | null => {
  const run = storedRunBlock(job);
  if (!run) return null;
  const costConfig = run.target.cost_config;
  if (costConfig != null && (!isRecord(costConfig) || FUNDING_ACCOUNTING_KEY in costConfig)) {
    return null;
  }
  return [...run.block, "cost_config", FUNDING_ACCOUNTING_KEY];
};

/**
 * Pins the legacy reversal policy and funding selector 1 in a copy's config, at the paths
 * `legacyReversalPinPath` and `legacyFundingPinPath` return. The paths are read from the pasted
 * job, not from the copy.
 */
const pinStoredRunDefaults = (job: TDT.ApiJob, config: object): void => {
  const reversalPinPath = legacyReversalPinPath(job);
  if (reversalPinPath) set(config, reversalPinPath, "legacy");
  const fundingPinPath = legacyFundingPinPath(job);
  if (fundingPinPath) set(config, fundingPinPath, 1);
};

/**
 * Fill Form admits the direct run types with a backtest-shaped config. This modal offers
 * Create Job for types outside that set instead of filling the run form.
 */
const FILL_FORM_JOB_TYPES = ["backtest", "optimization", "live"] as const;
type FillFormJob = Extract<TDT.ApiJob, { job_type_name: (typeof FILL_FORM_JOB_TYPES)[number] }>;

/**
 * Whether Fill Form accepts this job. The Fill Form button, its handler and the searched-policy
 * notice use this one test.
 */
const isFillFormJob = (job: TDT.ApiJob | null): job is FillFormJob =>
  job !== null && FILL_FORM_JOB_TYPES.some((jobType) => job.job_type_name === jobType);

/**
 * Build CommonSettingsData from a raw job for the RunRunnerPage form.
 */
const buildFormData = (
  job: FillFormJob,
  origin: TDT.ApiJobOrigin,
): CommonSettingsData & { config: string } => {
  const { config } = job;
  // The form's JSON takes a raw exit_config, which the helper fills from the source's exit
  // policy, pinning the legacy pair when neither key is requested. A live source is copied
  // unchanged, because the API gives live jobs no policy default.
  const { config: policyConfig } = cloneExitPolicy(job.job_type_name, config, "backtest");
  pinStoredRunDefaults(job, policyConfig);
  const portfolio = config.portfolio || {};
  // Capital and cumulative investment fill their own form fields; the rest of the portfolio
  // block has no field and rides along in the JSON. Cost settings stay nested so the Run page
  // forwards them as one block.
  const {
    initial_capital: _capital,
    cumulative_investment: _cumulative,
    ...portfolioRest
  } = portfolio;

  const cleanConfig = {
    strategy_config: config.strategy_config || {},
    exit_config: policyConfig.exit_config || {},
    ...(config.regime_config && { regime_config: config.regime_config }),
    ...pick(policyConfig, BACKTEST_ONLY_ENTRY_POLICY_KEYS),
    ...(policyConfig.cost_config && { cost_config: policyConfig.cost_config }),
    ...portfolioRest,
  };

  const formData: CommonSettingsData & { config: string } = {
    strategyId: job.strategy_id!, // validated in parseJobJson
    exchangeName: job.exchange_name || "",
    instrument: job.symbol_name || "",
    timeframe: job.period || "5m",
    startDateTime: job.from_ts || "",
    endDateTime: job.end_ts || "",
    investmentAmount: portfolio.initial_capital || 1000,
    cumulativeInvestment: portfolio.cumulative_investment ?? true,
    config: JSON.stringify(cleanConfig, null, 2),
    origin,
  };

  if (job.job_type_name === "live") {
    formData.exchangeEnvironment = (job.config.sandbox ? "qa" : "prod") as ExchangeEnvironment;
    formData.infisicalKeyPath = job.config.secrets_provider_config?.key_path || "";
    formData.infisicalSecretPath = job.config.secrets_provider_config?.secret_path || "";
  }

  return formData;
};

/**
 * Build an ApiJobCreate payload by picking only the fields the create endpoint accepts.
 * The config keeps the source's shape. A backtest, optimization or walk-forward source that
 * requests neither exit policy key gets the legacy pair pinned in that shape, and the legacy
 * reversal policy is pinned where `legacyReversalPinPath` names a path. The funding selector is
 * pinned to 1 where `legacyFundingPinPath` names a path.
 */
const buildJobCreatePayload = (job: TDT.ApiJob, origin: TDT.ApiJobOrigin): TDT.ApiJobCreate => {
  const { config } = cloneExitPolicy(job.job_type_name, job.config);
  pinStoredRunDefaults(job, config);
  return {
    ...pick(job, JOB_CREATE_FIELDS),
    config,
    metadata: { origin },
  } as TDT.ApiJobCreate;
};

interface PolicyKeysProps {
  keys: readonly ExitPolicyKey[];
}

/** Names exit policy keys as code, joined with "and". */
const PolicyKeys: React.FC<PolicyKeysProps> = ({ keys }) => (
  <>
    {keys.map((key, index) => (
      <React.Fragment key={key}>
        {index > 0 && " and "}
        <code>{key}</code>
      </React.Fragment>
    ))}
  </>
);

interface Props {
  visible: boolean;
  onClose: () => void;
}

export const FromJobModal: React.FC<Props> = ({ visible, onClose }) => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const { setBacktestFromExternal } = useBacktests();
  const { trackActiveJob, syncActiveJobs } = useJobPolling();
  const { getStrategyById } = useStrategies();

  const [jsonText, setJsonText] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedJob, setParsedJob] = useState<TDT.ApiJob | null>(null);
  const [creating, setCreating] = useState(false);
  const [filling, setFilling] = useState(false);
  const busy = creating || filling;

  // Moves on when the modal closes or unmounts. Escape, a backdrop click or the header button can
  // close it while an action awaits the id check; the action compares the value it started with
  // and stops, so a dismissed paste neither fills the form nor creates a job.
  const runRef = useRef(0);
  useEffect(
    () => () => {
      runRef.current += 1;
    },
    [],
  );

  /**
   * Run the id check for an action. Returns null when the check fails, with a toast unless the
   * modal closed or unmounted meanwhile.
   */
  const checkOrigin = async (job: TDT.ApiJob): Promise<TDT.ApiJobOrigin | null> => {
    const run = runRef.current;
    try {
      return await resolveOrigin(job);
    } catch (error) {
      if (runRef.current === run) {
        const message = getApiErrorMessage(error);
        showToast({ message: `Failed to check source job #${job.id}: ${message}`, type: "danger" });
      }
      return null;
    }
  };

  const handleJsonChange = (value: string) => {
    setJsonText(value);

    if (!value.trim()) {
      setParsedJob(null);
      setParseError(null);
      return;
    }

    const result = parseJobJson(value);
    if (result.error) {
      setParseError(result.error);
      setParsedJob(null);
    } else {
      setParsedJob(result.job);
      setParseError(null);
    }
  };

  const handleFillForm = async () => {
    if (!isFillFormJob(parsedJob)) return;

    const run = runRef.current;
    setFilling(true);
    const origin = await checkOrigin(parsedJob);
    // Closed or unmounted during the read: stop without touching state.
    if (runRef.current !== run) return;
    setFilling(false);
    if (!origin) return;

    const formData = buildFormData(parsedJob, origin);
    setBacktestFromExternal(formatBacktestDataFromSource(formData));

    const targetPath = parsedJob.job_type_name === "live" ? "/execution/new" : "/backtest/new";
    navigate(`${targetPath}?loadFrom=fromJob`);

    handleClose();
  };

  const handleCreateJob = async () => {
    if (!parsedJob) return;

    const run = runRef.current;
    setCreating(true);
    const origin = await checkOrigin(parsedJob);
    // Closed or unmounted during the read: stop without touching state.
    if (runRef.current !== run) return;
    if (!origin) {
      setCreating(false);
      return;
    }
    try {
      const payload = buildJobCreatePayload(parsedJob, origin);
      const createdJob = await tdApi.createJob(payload);

      if (!createdJob?.id) {
        throw new Error("Failed to create job — no ID returned");
      }

      const entityType =
        parsedJob.job_type_name === "live"
          ? "execution"
          : parsedJob.job_type_name === "backtest"
            ? "backtest"
            : parsedJob.job_type_name === "optimization"
              ? "optimization"
              : // Other job types get no entity link in this path.
                undefined;
      const source =
        origin.type === "duplicate" ? `source job #${parsedJob.id}` : "the pasted job JSON";
      const isScan = parsedJob.job_type_name === "scan";
      // Announce the returned job even if the modal closed during the POST. Request tracking for
      // unfinished jobs; the reducer drops a second entry for a tracked job. Navigation and modal
      // reset below run when this run of the modal is still current.
      if (!["completed", "failed", "cancelled"].includes(createdJob.status_name)) {
        trackActiveJob({
          jobId: createdJob.id,
          jobType: parsedJob.job_type_name,
          entityType,
          metadata: {
            title: ["From Job:", parsedJob.exchange_name, parsedJob.symbol_name]
              .filter(Boolean)
              .join(" "),
            description: isScan ? `Returned for ${source}` : `Created from ${source}`,
          },
        });
      }
      syncActiveJobs();

      const unlinked = origin.type === "manual" && parsedJob.id != null;
      let message: string;
      if (isScan) {
        if (origin.type === "duplicate" && createdJob.id === origin.source_job_id) {
          message =
            `Scan job #${createdJob.id} is the source job; the API returned it instead of creating an identical scan. ` +
            "Running it again requires a higher config.revision.";
        } else {
          message =
            `Scan job #${createdJob.id} returned for ${source}; ` +
            "the API returns an identical scan unless it failed or was cancelled" +
            (unlinked ? "; it is not linked to a source job." : ".");
        }
      } else {
        message = unlinked
          ? `Job #${createdJob.id} created from ${source}; it is not linked to a source job.`
          : `Job #${createdJob.id} created from ${source}`;
      }
      showToast({ message, type: "success" });

      if (runRef.current !== run) return;
      const listPath =
        parsedJob.job_type_name === "live"
          ? "/executions"
          : parsedJob.job_type_name === "backtest"
            ? "/backtests"
            : "/jobs";
      navigate(listPath);
      handleClose();
    } catch (error) {
      if (runRef.current !== run) return;
      const message = getApiErrorMessage(error);
      showToast({ message: `Failed to create job: ${message}`, type: "danger" });
    } finally {
      if (runRef.current === run) setCreating(false);
    }
  };

  const handleClose = () => {
    runRef.current += 1;
    setJsonText("");
    setParsedJob(null);
    setParseError(null);
    setCreating(false);
    setFilling(false);
    onClose();
  };

  const strategyName = parsedJob?.strategy_id
    ? (getStrategyById(parsedJob.strategy_id)?.name ?? null)
    : null;

  const exitPolicy = useMemo(
    () => (parsedJob ? classifyExitPolicy(parsedJob.job_type_name, parsedJob.config) : null),
    [parsedJob],
  );

  const pinsLegacyReversal = parsedJob !== null && legacyReversalPinPath(parsedJob) !== null;
  const pinsLegacyFunding = parsedJob !== null && legacyFundingPinPath(parsedJob) !== null;

  const JOB_TYPE_LABEL: Record<string, string> = {
    backtest: "Backtest",
    live: "Execution",
    optimization: "Optimization",
    cache: "Cache",
    walk_forward: "Walk-Forward",
    monte_carlo: "Monte Carlo",
    scan: "Scan",
  };

  return (
    <CModal className="FromJobModal" size="lg" visible={visible} onClose={handleClose}>
      <CModalHeader closeButton>
        <CModalTitle>Import from Job JSON</CModalTitle>
      </CModalHeader>

      <CModalBody>
        <CJsonTextArea
          value={jsonText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleJsonChange(e.target.value)}
          rows={14}
          placeholder="Paste a job JSON here..."
          disabled={busy}
        />

        {parseError && <div className="FromJobModal-error">{parseError}</div>}

        {parsedJob && (
          <div className="FromJobModal-summary">
            <div className="FromJobModal-summaryRow">
              <strong>Type:</strong>
              <span>{JOB_TYPE_LABEL[parsedJob.job_type_name] ?? parsedJob.job_type_name}</span>
            </div>
            {parsedJob.id != null && (
              <div className="FromJobModal-summaryRow">
                <strong>Source ID:</strong>
                <span>#{parsedJob.id}</span>
              </div>
            )}
            <div className="FromJobModal-summaryRow">
              <strong>Strategy:</strong>
              <span>
                {strategyName
                  ? `${strategyName} (#${parsedJob.strategy_id})`
                  : `#${parsedJob.strategy_id}`}
              </span>
            </div>
            {parsedJob.exchange_name && (
              <div className="FromJobModal-summaryRow">
                <strong>Exchange:</strong>
                <span>{parsedJob.exchange_name}</span>
              </div>
            )}
            {parsedJob.symbol_name && (
              <div className="FromJobModal-summaryRow">
                <strong>Symbol:</strong>
                <span>{parsedJob.symbol_name}</span>
              </div>
            )}
            {parsedJob.period && (
              <div className="FromJobModal-summaryRow">
                <strong>Timeframe:</strong>
                <span>{parsedJob.period}</span>
              </div>
            )}
            {parsedJob.from_ts && (
              <div className="FromJobModal-summaryRow">
                <strong>From:</strong>
                <span>{parsedJob.from_ts}</span>
              </div>
            )}
            {parsedJob.end_ts && (
              <div className="FromJobModal-summaryRow">
                <strong>To:</strong>
                <span>{parsedJob.end_ts}</span>
              </div>
            )}
          </div>
        )}

        {exitPolicy?.kind === "flag_only" && exitPolicy.versionFloats && (
          <div className="FromJobModal-policyNotice FromJobModal-flagOnlyNotice">
            The source job sets <code>exit_arbitration: true</code> without a pinned{" "}
            <code>exit_semantics_version</code>. The copy keeps it that way, so the engine resolves
            the version when the new job runs, and it can use a later version than the source job
            did.
          </div>
        )}

        {exitPolicy?.kind === "searched" && isFillFormJob(parsedJob) && (
          <div className="FromJobModal-policyNotice FromJobModal-searchedPolicyNotice">
            The source job searches <PolicyKeys keys={exitPolicy.searchedKeys} />.{" "}
            <PolicyKeys keys={exitPolicy.searchedKeys} />{" "}
            {exitPolicy.searchedKeys.length > 1 ? "are" : "is"} left out of the Fill Form JSON. The
            API stamps its current exit policy only if neither <code>exit_arbitration</code> nor{" "}
            <code>exit_semantics_version</code> remains requested. Create Job keeps the search.
          </div>
        )}

        {pinsLegacyReversal && (
          <div className="FromJobModal-policyNotice FromJobModal-reversalPinNotice">
            The source job does not name <code>cross_direction_reversal</code>, and the API reads a
            stored job without it as <code>legacy</code>. The copy pins <code>legacy</code>.
          </div>
        )}

        {pinsLegacyFunding && (
          <div className="FromJobModal-policyNotice FromJobModal-fundingPinNotice">
            The source job does not name <code>funding_accounting_version</code>. A stored job
            without it ran version 1. The copy names 1 to keep that accounting.
          </div>
        )}
      </CModalBody>

      <CModalFooter className="FromJobModal-footer">
        <div className="FromJobModal-footerActions">
          <CButton
            color="primary"
            disabled={!isFillFormJob(parsedJob) || busy}
            onClick={handleFillForm}
            title="Extract fields into the form for review before submitting"
          >
            {filling ? <CSpinnerText text="Checking source..." gap="2" /> : "Fill Form"}
          </CButton>
          <CButton
            color="success"
            disabled={!parsedJob || busy}
            onClick={handleCreateJob}
            title="Create the job directly on this environment"
          >
            {creating ? <CSpinnerText text="Creating..." gap="2" /> : "Create Job"}
          </CButton>
        </div>

        <CButton color="secondary" onClick={handleClose} disabled={busy}>
          Cancel
        </CButton>
      </CModalFooter>
    </CModal>
  );
};
